import { useState } from 'react';
import { UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Minus, Plus, Pencil } from 'lucide-react';
import {
  CareEnvironment,
  CarePhase,
  carePhaseDefList,
  carePhaseDefs,
  careEnvironmentsApplicableToPhase,
  careEnvironmentDefs,
  formatCurrency,
  FundingPolicyType,
} from '@shared';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Button,
  cn,
} from '@design-system';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge, CurrencyInput } from '@/components';
import { DialogModal } from '@/components/DialogModal';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { EditAssumptionsFormFields } from '../hooks';

interface CostEditModalProps {
  carePhase: CarePhase;
  onClose: () => void;
  onSave: (env: CareEnvironment, cost: number | null, useLocal: boolean) => void;
  phaseCosts: Client['phaseCosts'];
  selections: Partial<Record<CarePhase, CareEnvironment | null>>;
  costs: CareEnvironmentCosts;
  useLocalRate: Partial<Record<CareEnvironment, boolean>>;
  localCosts: LocalCareEnvironmentCosts;
}

function CostEditModal({
  carePhase,
  onClose,
  onSave,
  phaseCosts,
  selections,
  costs,
  useLocalRate,
  localCosts,
}: CostEditModalProps) {
  const phaseIndex = carePhaseDefs[carePhase].index;
  const env =
    selections[carePhase] ?? phaseCosts?.[phaseIndex]?.appliedCareEnvironment ?? null;

  const envDef = env != null ? careEnvironmentDefs[env] : null;
  const envLocalCosts = env != null ? localCosts[env] : null;

  const initialUseLocal = env != null ? (useLocalRate[env] ?? true) : true;
  const initialCost = env != null ? (costs[env] ?? null) : null;

  const [localUseLocalRate, setLocalUseLocalRate] = useState(initialUseLocal);
  const [localCost, setLocalCost] = useState<number | null>(initialCost);

  if (env == null || !envDef || !envLocalCosts) return null;

  const unitDisplay = env === CareEnvironment.home ? 'hr' : 'month';
  const hasCqnDiscount =
    envLocalCosts?.discount?.policyType === FundingPolicyType.careScoutCareAssurance;

  const handleToggleUseLocal = () => {
    const newUseLocal = !localUseLocalRate;
    setLocalUseLocalRate(newUseLocal);
    if (!newUseLocal && localCost == null) {
      setLocalCost(envLocalCosts?.rateAmount ?? null);
    }
  };

  const handleSave = () => {
    const costToSave = localUseLocalRate ? null : localCost;
    onSave(env, costToSave, localUseLocalRate);
    onClose();
  };

  return (
    <DialogModal
      open
      onClose={onClose}
      title={`Edit ${envDef.label} Cost`}
      subTitle=""
      width="md"
    >
      <div className="relative pb-8">
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 py-7">
          <div className="col-start-1 col-end-3 flex flex-col gap-2 pb-4">
            <h4 className="text-lg text-gray-900">
              Set your {env === CareEnvironment.home ? 'hourly' : 'monthly'} rate
            </h4>
            <div className="flex items-center gap-1 text-sm text-gray-700">
              <span>Default rate per {envLocalCosts?.rateLocaleDescription?.toLowerCase()}:</span>
              <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-800">
                {formatCurrency(envLocalCosts?.rateAmount)}/{unitDisplay}
              </span>
            </div>
          </div>
          <div>
            <CurrencyInput
              label={env === CareEnvironment.home ? 'CUSTOM HOURLY RATE' : 'CUSTOM MONTHLY RATE'}
              unit={env === CareEnvironment.home ? 'per hour' : 'per month'}
              placeholder="Enter dollars"
              value={localCost}
              onChange={(value: number) => setLocalCost(value)}
              disabled={localUseLocalRate}
            />
            <div className="mt-3 flex items-center gap-3">
              <Checkbox
                id={`cost-modal-use-local-rate-${env}`}
                checked={localUseLocalRate}
                onCheckedChange={handleToggleUseLocal}
              />
              <Label
                htmlFor={`cost-modal-use-local-rate-${env}`}
                className="flex flex-col gap-0.5 text-sm text-gray-700"
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">Use default rate</span>
                  <span className="text-gray-700">
                    ({formatCurrency(envLocalCosts?.rateAmount)}/{unitDisplay})
                  </span>
                </span>
                {hasCqnDiscount && (
                  <span className="block font-normal text-gray-700">
                    Default rate applies CQN savings. To turn off projected CQN savings,
                    uncheck this box and enter a custom rate.
                  </span>
                )}
              </Label>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-row-reverse">
        <Button
          type="button"
          onClick={handleSave}
          className="px-12 text-lg font-semibold sm:ml-3 sm:w-auto"
        >
          Done
        </Button>
      </div>
    </DialogModal>
  );
}

interface CareTimelineTableProps {
  watch: UseFormWatch<EditAssumptionsFormFields>;
  setValue: UseFormSetValue<EditAssumptionsFormFields>;
  previewClient: Client;
  updatePreviewClient: (updatedClient: Client) => void;
  isPastPhase: (phaseIndex: number) => boolean;
  selections: Partial<Record<CarePhase, CareEnvironment | null>>;
  selectEnvironment: (phase: CarePhase, env: CareEnvironment | null) => void;
  costs: CareEnvironmentCosts;
  useLocalRate: Partial<Record<CareEnvironment, boolean>>;
  localCosts: LocalCareEnvironmentCosts;
  saveCost: (env: CareEnvironment, cost: number | null, useLocal: boolean) => void;
  onResetToPredicted: () => void;
  isResetDisabled: boolean;
}

export function CareTimelineTable({
  watch,
  setValue,
  previewClient,
  updatePreviewClient,
  isPastPhase,
  selections,
  selectEnvironment,
  costs,
  useLocalRate,
  localCosts,
  saveCost,
  onResetToPredicted,
  isResetDisabled,
}: CareTimelineTableProps) {
  const [costModalPhase, setCostModalPhase] = useState<CarePhase | null>(null);

  const {
    intakeSurvey: { clientBirthYear },
    clientPhasePredictedStartYears,
    phaseCosts,
  } = previewClient;

  const updateDurationPreview = (phaseValue: CarePhase, newValue: number) => {
    const updatedCarePhaseDurations = {
      ...Object.fromEntries(
        carePhaseDefList.map(phaseDef => [phaseDef.value, watch(carePhaseDefs[phaseDef.value].key)]),
      ),
      [phaseValue]: newValue,
    };
    updatePreviewClient({
      ...previewClient,
      carePhaseDurationSelections: updatedCarePhaseDurations,
    });
  };

  const getUnitCostDisplay = (carePhase: CarePhase, phaseIndex: number) => {
    const env =
      selections[carePhase] ?? phaseCosts?.[phaseIndex]?.appliedCareEnvironment ?? null;
    if (env == null) return '-';

    const customCost = costs[env];
    const localRate = localCosts[env];
    const isHome = env === CareEnvironment.home;
    const unit = isHome ? 'hr' : 'mo';

    if (customCost != null) {
      return `${formatCurrency(customCost)}/${unit}`;
    }
    if (localRate?.rateAmount != null) {
      return `${formatCurrency(localRate.rateAmount)}/${unit}`;
    }
    return '-';
  };

  const getActiveEnvForPhase = (carePhase: CarePhase, phaseIndex: number) => {
    return selections[carePhase] ?? phaseCosts?.[phaseIndex]?.appliedCareEnvironment ?? null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Care Timeline</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onResetToPredicted}
          className={cn('h-7 px-2 text-xs', { invisible: isResetDisabled })}
          data-testid="care-timeline-reset-button"
        >
          Reset to Predicted
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50/50">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-gray-200 hover:bg-transparent">
              <TableHead className="w-[160px] pl-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Phase
              </TableHead>
              <TableHead className="w-[100px] text-xs font-semibold uppercase tracking-wider text-gray-500">
                Est. Begin
              </TableHead>
              <TableHead className="w-[180px] text-xs font-semibold uppercase tracking-wider text-gray-500">
                Environment
              </TableHead>
              <TableHead className="w-[200px] text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                Duration (mo)
              </TableHead>
              <TableHead className="w-[140px] pr-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                Unit Cost
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carePhaseDefList.map(carePhaseDef => {
              const carePhase = carePhaseDef.value;
              const phaseIndex = carePhaseDefs[carePhase].index;
              const phaseKey = carePhaseDefs[carePhase].key;
              const hidden = isPastPhase(phaseIndex);
              if (hidden) return null;

              const predictedPhaseStartYear = clientPhasePredictedStartYears?.[carePhase];
              const phaseAge = predictedPhaseStartYear
                ? predictedPhaseStartYear - clientBirthYear
                : null;

              const applicableCareSettings = careEnvironmentsApplicableToPhase[carePhase];
              const selectedEnv = selections[carePhase] ?? null;
              const selectedCarePhaseCosts = phaseCosts?.[phaseIndex];
              const recommendedEnv = selectedCarePhaseCosts?.recommendedCareEnvironment;
              const currentDuration = watch(phaseKey);
              const activeEnv = getActiveEnvForPhase(carePhase, phaseIndex);

              const MAX_DURATION_MONTHS = 120;
              const setDuration = (newValue: number) => {
                const clamped = Math.min(MAX_DURATION_MONTHS, Math.max(0, newValue));
                setValue(phaseKey, clamped, { shouldDirty: true });
                updateDurationPreview(carePhase, clamped);
              };

              return (
                <TableRow
                  key={carePhaseDef.key}
                  className="border-b border-gray-100 hover:bg-white/60"
                >
                  {/* Phase */}
                  <TableCell className="py-4 pl-4">
                    <span className="text-sm font-semibold text-gray-900">
                      {carePhaseDef.label}
                    </span>
                  </TableCell>

                  {/* Est. Begin */}
                  <TableCell className="py-4">
                    {phaseAge != null ? (
                      <Badge color="primary" label={`${Math.round(phaseAge)} yrs`} />
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </TableCell>

                  {/* Environment */}
                  <TableCell className="py-4">
                    <Select
                      value={selectedEnv != null ? String(selectedEnv) : ''}
                      onValueChange={value => {
                        selectEnvironment(carePhase, Number(value) as CareEnvironment);
                      }}
                    >
                      <SelectTrigger className="h-9 w-[160px] text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {applicableCareSettings.map(careSettingData => (
                          <SelectItem
                            key={careSettingData.dbId}
                            value={String(careSettingData.dbId)}
                          >
                            {careSettingData.label}
                            {careSettingData.dbId === recommendedEnv ? ' (Predicted)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Duration */}
                  <TableCell className="py-4">
                    <div className="flex items-center justify-center gap-1.5">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => setDuration(currentDuration - 1)}
                        disabled={currentDuration <= 0}
                        data-testid={`care-phase-${carePhaseDef.slug}-decrement`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <Input
                        type="number"
                        min={0}
                        max={MAX_DURATION_MONTHS}
                        className="h-8 w-16 text-center text-sm font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        value={currentDuration}
                        onChange={e => {
                          const parsed = parseInt(e.target.value, 10);
                          if (!isNaN(parsed)) setDuration(parsed);
                        }}
                        data-testid={`care-phase-${carePhaseDef.slug}-input`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => setDuration(currentDuration + 1)}
                        disabled={currentDuration >= MAX_DURATION_MONTHS}
                        data-testid={`care-phase-${carePhaseDef.slug}-increment`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>

                  {/* Unit Cost */}
                  <TableCell className="py-4 pr-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-sm font-medium text-gray-900">
                        {getUnitCostDisplay(carePhase, phaseIndex)}
                      </span>
                      {activeEnv != null && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-gray-600"
                          onClick={() => setCostModalPhase(carePhase)}
                          aria-label={`Edit ${carePhaseDef.label} cost`}
                          data-testid={`care-phase-${carePhaseDef.slug}-edit-cost`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {costModalPhase != null && (
        <CostEditModal
          carePhase={costModalPhase}
          onClose={() => setCostModalPhase(null)}
          onSave={saveCost}
          phaseCosts={phaseCosts}
          selections={selections}
          costs={costs}
          useLocalRate={useLocalRate}
          localCosts={localCosts}
        />
      )}
    </div>
  );
}
